// ---------------------------------------------------------------------------
// Block Definition: gantt
//
// Swim-lane workplan chart: groups (phases) -> lanes (workstreams) -> bars
// (tasks), plus a decision-gate milestone per group. Entirely code-derived
// from a structured JSON pin (the MCP's build_engagement_plan output) —
// phase/task scheduling is already resolved deterministically upstream
// (buildEngagementPlan.js's scheduleTasks), so this block never needs an LLM
// call; extractStructured just re-shapes plan.phases into the renderer's
// group/lane/bar contract.
// ---------------------------------------------------------------------------

function laneKey(phaseId, workstreamId) {
  return `${phaseId}__${workstreamId ?? "general"}`;
}

/** Project one plan phase into a {id, label, committed, start, end, note,
 *  lanes, milestone} group. Lanes come from the phase's workstream list
 *  (so an empty, not-yet-scheduled workstream still shows as a row); tasks
 *  with no workstream_id fall into a synthesized "General" lane. Tasks with
 *  no resolvable week_range (scheduleTasks couldn't place them) are skipped
 *  rather than mis-positioned. */
function buildGroup(phase) {
  const lanesById = new Map();
  const ensureLane = (id, label, role) => {
    if (!lanesById.has(id)) lanesById.set(id, { id, label, role: role ?? null, bars: [] });
    return lanesById.get(id);
  };

  for (const w of phase.workstreams ?? []) {
    ensureLane(laneKey(phase.id, w.id), w.label, w.role);
  }

  for (const t of phase.tasks ?? []) {
    if (!Array.isArray(t.week_range) || t.week_range[0] == null) continue;
    const start = t.week_range[0];
    const end = t.week_range[1] ?? start;
    const ws = t.workstream_id ? (phase.workstreams ?? []).find((w) => w.id === t.workstream_id) : null;
    const lane = t.workstream_id
      ? ensureLane(laneKey(phase.id, t.workstream_id), ws?.label ?? t.workstream_id, ws?.role)
      : ensureLane(laneKey(phase.id, null), "General", null);
    lane.bars.push({
      id: `${lane.id}__${lane.bars.length}`,
      label: t.task ?? "",
      start,
      duration: Math.max(1, end - start + 1),
      work_type: t.work_type ?? null,
      owner: t.owner ?? null,
    });
  }

  const lanes = [...lanesById.values()];
  for (const l of lanes) l.bars.sort((a, b) => a.start - b.start);

  return {
    id: phase.id,
    label: phase.name,
    committed: !!phase.in_current_scope,
    start: Array.isArray(phase.week_range) ? phase.week_range[0] : null,
    end: Array.isArray(phase.week_range) ? phase.week_range[1] : null,
    note: phase.in_current_scope ? null : (phase.purpose ?? null),
    lanes,
    outputs: phase.in_current_scope && Array.isArray(phase.outputs) ? phase.outputs : [],
  };
}

export const ganttDef = {
  type: "gantt",
  label: "Gantt / Workplan",
  description: "Swim-lane workplan chart: phases as groups, workstreams as lane headers, each task its own row, decision gates and outputs as milestones/detail.",

  crystallizer: {
    // Always resolved from structured plan JSON — never an LLM target.
    extractionPhases() { return ["structured"]; },

    // structured._raw is the plan pin's parsed JSON (build_engagement_plan's
    // output). Not committed (no in_current_scope phase with a week_range)
    // means there's nothing to schedule yet — return null so the renderer's
    // empty-state shows instead of an empty grid.
    extractStructured(block, structured) {
      const raw = structured && structured._raw;
      const phases = Array.isArray(raw?.phases) ? raw.phases : null;
      if (!phases || phases.length === 0) return null;

      const committed = phases.filter((p) => p.in_current_scope && Array.isArray(p.week_range));
      const totalWeeks = committed.length > 0 ? Math.max(...committed.map((p) => p.week_range[1])) : 0;
      if (totalWeeks <= 0) return null;

      // An uncommitted next phase with no workstreams/tasks has nothing
      // real to show as a swim lane — skip it entirely rather than
      // fabricating a placeholder band for a phase we know nothing about yet.
      const hasDetail = (p) => (p.workstreams?.length ?? 0) > 0 || (p.tasks?.length ?? 0) > 0;
      const groups = phases
        .filter((phase) => phase.in_current_scope || hasDetail(phase))
        .map(buildGroup);

      return { total_weeks: totalWeeks, groups };
    },

    buildDataSchema() { return { type: "object", additionalProperties: true }; },
    buildShapeDescription() { return "{ ... }"; },
    isBlockLevelTarget() { return false; },
    // Always resolved in Phase 1 (structured) — never contributes to an LLM response_format.
    skipFromPinSchema() { return true; },
    // NOT the same "isCodeDerived" as tile_row's derive/heatmap's count_field —
    // that flag makes the crystallizer's tree-walker zero out the block's pins
    // (collectTargetsByPin: `_codeDerived ? [] : pd(effectivePins)`), because
    // those blocks resolve via computeDeterministic()/recordsFor(), which falls
    // back to the block's own `.pins` directly. The "structured" extraction
    // phase used here (extractStructured, matching text_block's all-extract
    // mode) only ever reads group.pins — zeroing them means extractStructured
    // never gets called at all. false here is what actually keeps this block
    // deterministic/LLM-free; true silently breaks it.
    isCodeDerived() { return false; },
    collectHints() { return []; },
    buildPromptSupplement() { return ""; },
    postProcess(data) { return data; },
  },

  renderer: null, // wired by ui/src/blockRegistryInit.js

  editor: {
    icon: "gantt-chart",
    category: "data-visualization",
    defaultTemplate: { type: "gantt", pins: ["plan"] },
    configFields: [
      { name: "pins", type: "tags", label: "Source pins", help: "Routes this chart to the structured plan pin during crystallize." },
      { name: "proportion", type: "number", label: "Proportion weight", step: 0.1 },
      { name: "label_column_width", type: "number", label: "Lane label column width (0-1)", step: 0.01, defaultValue: 0.22 },
      { name: "lane_row_height", type: "number", label: "Lane row height (px)", defaultValue: 32 },
    ],
  },
};
